import type { TFunction } from 'i18next';

import { IRiskCenterTabsFilter } from '../../types/innerLayout/risk_center/header';
import {
  addBlackList,
  editBlackList,
  getBlacklistData,
  removeBlackList,
} from '../../services/inner_layout/risk_center/blacklist';
import {
  addWhiteList,
  editWhiteList,
  getWhitelistData,
  removeWhiteList,
} from '../../services/inner_layout/risk_center/whitelist';
import {
  addRule,
  editRule,
  getRulesListData,
  removeRule,
} from '../../services/inner_layout/risk_center/rules';
import {
  generateModalKeys,
  getValidations,
} from '../../views/pages/inner_layout/RiskCenter/utils/modals';
import {
  getSingleBlacklistItemDetails,
  getSingleRuleItemDetails,
  getSingleWhitelistItemDetails,
} from '../../services/inner_layout/risk_center';
import { TRiskCenterTypes } from '@app/dashboard/types/innerLayout/risk_center';
import { SYSTEM_EVENTS_GROUPING_CODES } from '../tracking_events/system';

export const RISK_CENTER_TAB_FILTERS = (
  t: TFunction
): IRiskCenterTabsFilter[] => [
  {
    id: 'blacklist',
    name: 'blacklist',
    label: t('innerLayout.riskCenter.blacklist.title'),
    hintDescription: t('innerLayout.riskCenter.blacklist.blacklistDescription'),
    btnLabel: t('innerLayout.riskCenter.blacklist.addBtn'),
  },
  {
    id: 'whitelist',
    name: 'whitelist',
    label: t('innerLayout.riskCenter.whitelist.title'),
    hintDescription: t('innerLayout.riskCenter.whitelist.whitelistDescription'),
    btnLabel: t('innerLayout.riskCenter.whitelist.addBtn'),
  },
  {
    id: 'rules',
    name: 'rules',
    label: t('innerLayout.riskCenter.rules.title'),
    hintDescription: t('innerLayout.riskCenter.rules.ruleDescription'),
    btnLabel: t('innerLayout.riskCenter.rules.addBtn'),
  },
];

export const RISK_CENTER_TYPES = {
  BLACKLIST: 'blacklist',
  WHITELIST: 'whitelist',
  RULES: 'rules',
} as const;

export const TABLE_HEADER_DEFINITIONS = (t: TFunction, lang: string) => ({
  [RISK_CENTER_TYPES.BLACKLIST]: [
    {
      name: t('innerLayout.riskCenter.blacklist.table.headers.id'),
      key: 'id',
      type: 'text',
      id: 'id',
      width: '20%',
    },
    {
      name: t('innerLayout.riskCenter.blacklist.table.headers.blacklistType'),
      key: 'blacklist_type',
      type: 'text',
      id: 'blacklist_type',
      width: '20%',
    },
    {
      name: t('innerLayout.riskCenter.blacklist.table.headers.blacklistName'),
      key: 'reason',
      type: 'text',
      id: 'reason',
      width: '20%',
    },
    {
      name: t('innerLayout.riskCenter.blacklist.table.headers.active'),
      id: 'active',
      key: 'active',
      type: 'badge',
      labelComparingValue: 'active',
      label: {
        true: {
          color: 'green',
          text: t('innerLayout.common.yes'),
        },
        false: {
          color: 'red',
          text: t('innerLayout.common.no'),
        },
      },
      format: (obj: any, value: any) => {
        return value === true ? 'true' : 'false';
      },
      width: '20%',
    },
    {
      name: t('innerLayout.riskCenter.blacklist.table.headers.createdAt'),
      key: 'created',
      type: 'date',
      id: 'created',
      width: '20%',
      hintText: 'created',
    },
  ],
  [RISK_CENTER_TYPES.WHITELIST]: [
    {
      name: t('innerLayout.riskCenter.whitelist.table.headers.id'),
      key: 'id',
      type: 'text',
      id: 'id',
      width: '20%',
    },
    {
      name: t('innerLayout.riskCenter.whitelist.table.headers.whitelistType'),
      key: 'whitelist_type',
      type: 'text',
      id: 'whitelist_type',
      width: '20%',
    },
    {
      name: t('innerLayout.riskCenter.whitelist.table.headers.whitelistName'),
      key: 'reason',
      type: 'text',
      id: 'reason',
      width: '20%',
    },
    {
      name: t('innerLayout.riskCenter.whitelist.table.headers.active'),
      key: 'active',
      type: 'badge',
      id: 'active',
      labelComparingValue: 'active',
      label: {
        true: {
          color: 'green',
          text: t('innerLayout.common.yes'),
        },
        false: {
          color: 'red',
          text: t('innerLayout.common.no'),
        },
      },
      format: (obj: any, value: any) => {
        return value === true ? 'true' : 'false';
      },
      width: '20%',
    },
    {
      name: t('innerLayout.riskCenter.whitelist.table.headers.createdAt'),
      key: 'created',
      type: 'date',
      id: 'created',
      width: '20%',
      hintText: 'created',
    },
  ],
  [RISK_CENTER_TYPES.RULES]: [
    {
      name: t('innerLayout.riskCenter.rules.table.headers.rule'),
      key: 'rule.description',
      type: 'text',
      id: 'description',
      render: (value: string, row: any) => {
        return lang === 'ar' ? row.rule.description_ar : row.rule.description;
      },
      width: '40%',
    },
    {
      name: t('innerLayout.riskCenter.rules.table.headers.priority'),
      key: 'priority',
      type: 'text',
      id: 'priority',
      width: '20%',
    },
    {
      name: t('innerLayout.riskCenter.rules.table.headers.active'),
      key: 'active',
      type: 'badge',
      id: 'active',
      labelComparingValue: 'active',
      label: {
        true: {
          color: 'green',
          text: t('innerLayout.common.yes'),
        },
        false: {
          color: 'red',
          text: t('innerLayout.common.no'),
        },
      },
      format: (obj: any, value: any) => {
        return value === true ? 'true' : 'false';
      },
      width: '20%',
    },
    {
      name: t('innerLayout.riskCenter.rules.table.headers.createdAt'),
      key: 'created',
      type: 'date',
      id: 'created',
      width: '20%',
      hintText: 'created',
    },
  ],
});

export const RISK_CENTER_APIS_MAP = {
  [RISK_CENTER_TYPES.BLACKLIST]: {
    FETCH_DATA_API: getBlacklistData,
    EMPTY_STATE: {
      EMPTY_STATE_TEXT_KEY:
        'innerLayout.riskCenter.blacklist.body.noBlacklists',
      EMPTY_STATE_HINT_KEY:
        'innerLayout.riskCenter.blacklist.body.noBlacklistsHint',
    },
  },
  [RISK_CENTER_TYPES.WHITELIST]: {
    FETCH_DATA_API: getWhitelistData,
    EMPTY_STATE: {
      EMPTY_STATE_TEXT_KEY:
        'innerLayout.riskCenter.whitelist.body.noWhitelists',
      EMPTY_STATE_HINT_KEY:
        'innerLayout.riskCenter.whitelist.body.noWhitelistsHint',
    },
  },
  [RISK_CENTER_TYPES.RULES]: {
    FETCH_DATA_API: getRulesListData,
    EMPTY_STATE: {
      EMPTY_STATE_TEXT_KEY: 'innerLayout.riskCenter.rules.body.noRules',
      EMPTY_STATE_HINT_KEY: 'innerLayout.riskCenter.rules.body.noRulesHint',
    },
  },
};

export const ADD_EDIT_MODAL_CONFIG = {
  [RISK_CENTER_TYPES.BLACKLIST]: generateModalKeys({
    key: RISK_CENTER_TYPES.BLACKLIST,
  }),
  [RISK_CENTER_TYPES.WHITELIST]: generateModalKeys({
    key: RISK_CENTER_TYPES.WHITELIST,
  }),
  [RISK_CENTER_TYPES.RULES]: generateModalKeys({
    key: RISK_CENTER_TYPES.RULES,
    includeProp: false,
  }),
};

export const LIST_TYPE_OPTIONS = (t: TFunction) => [
  {
    name: t('innerLayout.riskCenter.listTypeOptions.email'),
    value: 'EL',
  },
  {
    name: t('innerLayout.riskCenter.listTypeOptions.phone'),
    value: 'PH',
  },
  {
    name: t('innerLayout.riskCenter.listTypeOptions.cardNumber'),
    value: 'PN',
  },
  {
    name: t('innerLayout.riskCenter.listTypeOptions.ip'),
    value: 'IP',
  },
  {
    name: t('innerLayout.riskCenter.listTypeOptions.fingerprint'),
    value: 'FP',
  },
  {
    name: t('innerLayout.riskCenter.listTypeOptions.country'),
    value: 'CO',
  },
  {
    name: t('innerLayout.riskCenter.listTypeOptions.bin'),
    value: 'BN',
  },
];

export const SINGLE_DETAILS_LABELS = (t: TFunction) => ({
  [RISK_CENTER_TYPES.BLACKLIST]: {
    title: t('innerLayout.riskCenter.blacklist.singleDetails.title'),
    labels: {
      idLabel: t(
        'innerLayout.riskCenter.blacklist.singleDetails.labels.idLabel'
      ),
      blacklistTypeLabel: t(
        'innerLayout.riskCenter.blacklist.singleDetails.labels.typeLabel'
      ),
      reasonLabel: t(
        'innerLayout.riskCenter.blacklist.singleDetails.labels.reasonLabel'
      ),
      dataLabel: t(
        'innerLayout.riskCenter.blacklist.singleDetails.labels.dataLabel'
      ),
      activeLabel: t(
        'innerLayout.riskCenter.blacklist.singleDetails.labels.activeLabel'
      ),
      createdAtLabel: t(
        'innerLayout.riskCenter.blacklist.singleDetails.labels.createdAtLabel'
      ),
    },
  },
  [RISK_CENTER_TYPES.WHITELIST]: {
    title: t('innerLayout.riskCenter.whitelist.singleDetails.title'),
    labels: {
      idLabel: t(
        'innerLayout.riskCenter.whitelist.singleDetails.labels.idLabel'
      ),
      whitelistTypeLabel: t(
        'innerLayout.riskCenter.whitelist.singleDetails.labels.typeLabel'
      ),
      dataLabel: t(
        'innerLayout.riskCenter.whitelist.singleDetails.labels.dataLabel'
      ),
      reasonLabel: t(
        'innerLayout.riskCenter.whitelist.singleDetails.labels.reasonLabel'
      ),
      activeLabel: t(
        'innerLayout.riskCenter.whitelist.singleDetails.labels.activeLabel'
      ),
      createdAtLabel: t(
        'innerLayout.riskCenter.whitelist.singleDetails.labels.createdAtLabel'
      ),
    },
  },
  [RISK_CENTER_TYPES.RULES]: {
    title: t('innerLayout.riskCenter.rules.singleDetails.title'),
    labels: {
      ruleLabel: t(
        'innerLayout.riskCenter.rules.singleDetails.labels.ruleLabel'
      ),
      priorityLabel: t(
        'innerLayout.riskCenter.rules.singleDetails.labels.priorityLabel'
      ),
      createdAtLabel: t(
        'innerLayout.riskCenter.rules.singleDetails.labels.createdAtLabel'
      ),
      activeLabel: t(
        'innerLayout.riskCenter.rules.singleDetails.labels.activeLabel'
      ),
      idLabel: t('innerLayout.riskCenter.rules.singleDetails.labels.idLabel'),
    },
  },
});

export const API_SINGLE_DETAILS_MAP = {
  [RISK_CENTER_TYPES.BLACKLIST]: getSingleBlacklistItemDetails,
  [RISK_CENTER_TYPES.WHITELIST]: getSingleWhitelistItemDetails,
  [RISK_CENTER_TYPES.RULES]: getSingleRuleItemDetails,
};

export const LIST_FIELD_TYPE = {
  EMAIL: 'EL',
  PHONE: 'PH',
  CARD: 'PN',
  IP: 'IP',
  FINGERPRINT: 'FP',
  BIN: 'BN',
  COUNTRY: 'CO',
  'CARD NUMBER': 'PN',
} as const;

export const INITIAL_ADD_EDIT_FIELDS_STATE = (t: TFunction) => ({
  [LIST_FIELD_TYPE.EMAIL]: {
    value: '',
    isValid: undefined,
    errorMsg: '',
    validations: getValidations(t).EL,
  },
  [LIST_FIELD_TYPE.PHONE]: {
    value: undefined,
    isValid: undefined,
    errorMsg: '',
    validations: getValidations(t).PH,
  },
  [LIST_FIELD_TYPE.CARD]: {
    value: undefined,
    isValid: undefined,
    errorMsg: '',
    validations: getValidations(t).PN,
  },
  [LIST_FIELD_TYPE.IP]: {
    value: '',
    isValid: undefined,
    errorMsg: '',
    validations: getValidations(t).IP,
  },
  [LIST_FIELD_TYPE.FINGERPRINT]: {
    value: '',
    isValid: undefined,
    errorMsg: '',
    validations: getValidations(t).FP,
  },
  [LIST_FIELD_TYPE.BIN]: {
    value: '',
    isValid: undefined,
    errorMsg: '',
    validations: getValidations(t).BN,
  },
  [LIST_FIELD_TYPE.COUNTRY]: {
    value: '',
    isValid: undefined,
    errorMsg: '',
    validations: getValidations(t).CO,
  },
});

export const ACTIONS = {
  ADD: 'add',
  EDIT: 'edit',
  DELETE: 'delete',
  DEACTIVATE: 'deactivate',
  ACTIVATE: 'activate',
} as const;

export const ADD_EDIT_MODAL_API_MAP = {
  [ACTIONS.ADD]: {
    [RISK_CENTER_TYPES.BLACKLIST]: addBlackList,
    [RISK_CENTER_TYPES.WHITELIST]: addWhiteList,
    [RISK_CENTER_TYPES.RULES]: addRule,
  },
  [ACTIONS.EDIT]: {
    [RISK_CENTER_TYPES.BLACKLIST]: editBlackList,
    [RISK_CENTER_TYPES.WHITELIST]: editWhiteList,
    [RISK_CENTER_TYPES.RULES]: editRule,
  },
  [ACTIONS.DELETE]: {
    [RISK_CENTER_TYPES.BLACKLIST]: removeBlackList,
    [RISK_CENTER_TYPES.WHITELIST]: removeWhiteList,
    [RISK_CENTER_TYPES.RULES]: removeRule,
  },
};

export const CONFIRMATION_MODALS_CONFIG = (
  type: TRiskCenterTypes,
  t: TFunction
) => ({
  [ACTIONS.DEACTIVATE]: {
    TITLE: t(
      `innerLayout.riskCenter.${type}.singleDetails.modals.deactivation.deactivate`
    ),
    SUBTITLE: t(
      `innerLayout.riskCenter.${type}.singleDetails.modals.deactivation.deactivateText`
    ),
    CONFIRMATION_BTN: t(
      `innerLayout.riskCenter.${type}.singleDetails.modals.deactivation.yesDeactivate`
    ),
    API_MAPPER: ADD_EDIT_MODAL_API_MAP[ACTIONS.EDIT][type],
    SUCCESS_MESSAGE: t(
      `innerLayout.riskCenter.${type}.singleDetails.modals.deactivation.successMessage`
    ),
    TRACKING_CODE: (SYSTEM_EVENTS_GROUPING_CODES.RISK_CENTER.DETAILS as any)[type.toUpperCase()].DEACTIVATE
  },
  [ACTIONS.ACTIVATE]: {
    TITLE: t(
      `innerLayout.riskCenter.${type}.singleDetails.modals.activation.activate`
    ),
    SUBTITLE: t(
      `innerLayout.riskCenter.${type}.singleDetails.modals.activation.activateText`
    ),
    CONFIRMATION_BTN: t(
      `innerLayout.riskCenter.${type}.singleDetails.modals.activation.yesActivate`
    ),
    API_MAPPER: ADD_EDIT_MODAL_API_MAP[ACTIONS.EDIT][type],
    SUCCESS_MESSAGE: t(
      `innerLayout.riskCenter.${type}.singleDetails.modals.activation.successMessage`
    ),
    TRACKING_CODE: (SYSTEM_EVENTS_GROUPING_CODES.RISK_CENTER.DETAILS as any)[type.toUpperCase()].ACTIVATE
  },
  [ACTIONS.DELETE]: {
    TITLE: t(
      `innerLayout.riskCenter.${type}.singleDetails.modals.deletion.delete`
    ),
    SUBTITLE: t(
      `innerLayout.riskCenter.${type}.singleDetails.modals.deletion.deleteText`
    ),
    CONFIRMATION_BTN: t(
      `innerLayout.riskCenter.${type}.singleDetails.modals.deletion.yesDelete`
    ),
    API_MAPPER: ADD_EDIT_MODAL_API_MAP[ACTIONS.DELETE][type],
    SUCCESS_MESSAGE: t(
      `innerLayout.riskCenter.${type}.singleDetails.modals.deletion.successMessage`
    ),
    TRACKING_CODE: (SYSTEM_EVENTS_GROUPING_CODES.RISK_CENTER.DETAILS as any)[type.toUpperCase()].DELETE
  },
});

export const FILTERS_CONFIG_MAP = (t: TFunction) => ({
  [RISK_CENTER_TYPES.BLACKLIST]: {
    FILTER_KEY: 'blacklist_type',
    FILTER_NAME: t('innerLayout.riskCenter.filters.blacklistType'),
  },
  [RISK_CENTER_TYPES.WHITELIST]: {
    FILTER_KEY: 'whitelist_type',
    FILTER_NAME: t('innerLayout.riskCenter.filters.whitelistType'),
  },
  [RISK_CENTER_TYPES.RULES]: {
    FILTER_KEY: 'rule_type',
    FILTER_NAME: t('innerLayout.riskCenter.filters.ruleType'),
  },
});

export const VALIDATION_REQUIRED_MESSAGE = (t: TFunction) =>
  t('errors.required');
